import { useCallback, useMemo, useState } from 'react';

export const useEntitySelections = () => {
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set());

  const selectedEntitiesCount = useMemo(() => selectedEntities.size, [selectedEntities]);
  const hasSelectedEntities = useMemo(() => selectedEntities.size > 0, [selectedEntities]);

  const isEntitySelected = useCallback(
    (id: string) => selectedEntities.has(id),
    [selectedEntities]
  );

  const isAnyEntitySelected = useCallback(
    (ids: string[]) => ids.some((id) => isEntitySelected(id)),
    [isEntitySelected]
  );

  const setEntities = useCallback(
    (entities: string[]) => {
      setSelectedEntities((prev) => {
        const toSave = new Set(prev);
        entities.forEach((entity) => toSave.add(entity));
        return toSave;
      });
    },
    [setSelectedEntities]
  );

  const removeEntities = useCallback(
    (entities: string[]) => {
      setSelectedEntities((prev) => {
        const toSave = new Set(prev);

        entities.forEach((entity) => toSave.delete(entity));
        return toSave;
      });
    },
    [setSelectedEntities]
  );

  const removeAllEntities = useCallback(() => {
    setSelectedEntities(new Set());
  }, [setSelectedEntities]);

  const setEntity = useCallback(
    (entity: string) => {
      setSelectedEntities((prev) => {
        const toSave = new Set(prev);
        if (toSave.has(entity)) {
          toSave.delete(entity);
          return toSave;
        }
        toSave.add(entity);
        return toSave;
      });
    },
    [setSelectedEntities]
  );

  const contextValue = useMemo(
    () => ({
      isEntitySelected,
      isAnyEntitySelected,
      hasSelectedEntities,
      selectedEntities,
      setEntities,
      removeEntities,
      removeAllEntities,
      setEntity,
      selectedEntitiesCount,
      setSelectedEntities,
    }),
    [
      isEntitySelected,
      isAnyEntitySelected,
      selectedEntities,
      setEntities,
      setEntity,
      removeEntities,
      removeAllEntities,
      hasSelectedEntities,
      selectedEntitiesCount,
      setSelectedEntities,
    ]
  );

  return contextValue;
};
